import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Home, MessageSquare, LayoutGrid, CheckCircle, Bot, Search, ChevronDown, CircleUser } from 'lucide-react';
import { directionForLanguage } from '../../i18n/config';
import './BottomNav.css';

export type NavTab = 'review' | 'editor' | 'agent' | 'ask' | 'clauses' | 'proofread' | 'profile';

interface BottomNavProps {
  activeTab: NavTab;
  onTabChange: (tab: NavTab) => void;
  /** Cycle trailing dots on the Review label while the document is being scanned. */
  reviewBusy?: boolean;
}

function useCyclingDots(active: boolean, intervalMs = 450): string {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!active) {
      setCount(0);
      return;
    }
    const id = window.setInterval(() => {
      setCount((n) => (n + 1) % 4);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs]);
  return active ? '.'.repeat(count) : '';
}

export const BottomNav: React.FC<BottomNavProps> = ({ activeTab, onTabChange, reviewBusy = false }) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const reviewBusyDots = useCyclingDots(reviewBusy);
  const [showEditorMenu, setShowEditorMenu] = React.useState(false);
  const editorMenuRef = React.useRef<HTMLDivElement | null>(null);

  const centerMode: 'editor' | 'agent' | 'ask' =
    activeTab === 'ask' ? 'ask' : activeTab === 'agent' ? 'agent' : 'editor';

  const centerIcon =
    centerMode === 'ask' ? Search : centerMode === 'agent' ? Bot : MessageSquare;
  const centerLabel =
    centerMode === 'ask' ? t('nav.ask') : centerMode === 'agent' ? t('nav.agent') : t('nav.editor');

  React.useEffect(() => {
    if (!showEditorMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!editorMenuRef.current) return;
      if (!editorMenuRef.current.contains(event.target as Node)) {
        setShowEditorMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEditorMenu]);

  const handleAgentClick = () => {
    onTabChange('agent');
    setShowEditorMenu(false);
  };

  return (
    <div className="bottom-nav">
      <button
        className={`nav-item ${activeTab === 'review' ? 'active' : ''} ${reviewBusy ? 'nav-item-busy' : ''}`}
        onClick={() => onTabChange('review')}
        aria-busy={reviewBusy || undefined}
      >
        <Home className="nav-icon" size={20} />
        <span className="nav-label" dir={chromeDir}>
          {t('nav.review')}
          {reviewBusy ? (
            <span className="nav-busy-dots" aria-hidden="true">
              {reviewBusyDots}
            </span>
          ) : null}
        </span>
      </button>
      <div className="nav-item-group" ref={editorMenuRef}>
        {showEditorMenu && (
          <div className="nav-dropdown-menu" dir={chromeDir} role="menu" aria-label={t('nav.editorOptions')}>
            <button
              className="nav-dropdown-item"
              onClick={() => {
                onTabChange('editor');
                setShowEditorMenu(false);
              }}
              role="menuitem"
            >
              <MessageSquare className="nav-dropdown-icon" size={14} />
              <span>{t('nav.editor')}</span>
            </button>
            <button
              className="nav-dropdown-item"
              onClick={handleAgentClick}
              role="menuitem"
            >
              <Bot className="nav-dropdown-icon" size={14} />
              <span>{t('nav.agent')}</span>
            </button>
            <button
              className="nav-dropdown-item"
              onClick={() => {
                onTabChange('ask');
                setShowEditorMenu(false);
              }}
              role="menuitem"
            >
              <Search className="nav-dropdown-icon" size={14} />
              <span>{t('nav.ask')}</span>
            </button>
          </div>
        )}
        <button
          className={`nav-item ${activeTab === 'editor' || activeTab === 'agent' || activeTab === 'ask' ? 'active' : ''}`}
          onClick={() => setShowEditorMenu((prev) => !prev)}
          aria-expanded={showEditorMenu}
          aria-haspopup="menu"
        >
          {React.createElement(centerIcon, { className: 'nav-icon', size: 20 })}
          <span className="nav-label-with-caret">
            <span className="nav-label">{centerLabel}</span>
            <ChevronDown className={`nav-caret ${showEditorMenu ? 'open' : ''}`} size={12} />
          </span>
        </button>
      </div>
      <button
        className={`nav-item ${activeTab === 'clauses' ? 'active' : ''}`}
        onClick={() => onTabChange('clauses')}
      >
        <LayoutGrid className="nav-icon" size={20} />
        <span className="nav-label">{t('nav.solutions')}</span>
      </button>
      <button
        className={`nav-item ${activeTab === 'proofread' ? 'active' : ''}`}
        onClick={() => onTabChange('proofread')}
      >
        <CheckCircle className="nav-icon" size={20} />
        <span className="nav-label">{t('nav.proofread')}</span>
      </button>
      <button
        type="button"
        className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
        onClick={() => {
          setShowEditorMenu(false);
          onTabChange('profile');
        }}
        aria-label={t('nav.profile')}
        title={t('nav.profile')}
      >
        <CircleUser className="nav-icon" size={20} />
        <span className="nav-label">{t('nav.profile')}</span>
      </button>
    </div>
  );
};

